import React, { useState } from 'react';
import useSWR from 'swr';
import {
  Alert, Box, Card, CardContent, Chip, Collapse, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { ambil, pesanGalat, denganCabang, tglIso, labelTgl, jam, rupiah, WARNA_STATUS, LABEL_STATUS } from './bersama';

// Tab Kehadiran: status per hari tiap karyawan, rekap, dan hitung gaji dari absensi (maks. 62 hari).

const KOLOM = [
  ['hadir', 'Hadir'], ['setengah', '½ hari'], ['alfa', 'Alfa'], ['izin', 'Izin'], ['sakit', 'Sakit'], ['cuti', 'Cuti'],
  ['libur_nasional', 'Libur nas.'], ['telat', 'Telat'], ['pulangCepat', 'Pulang cepat'], ['belum_pulang', 'Lupa pulang'],
];

const ketHari = (h) => {
  const bag = [];
  if (h.shift) bag.push(`${h.shift.nama} ${jam(h.shift.jamMulai)}–${jam(h.shift.jamSelesai)}`);
  if (h.masuk) bag.push(`masuk ${jam(h.masuk)}${h.keluar ? `, pulang ${jam(h.keluar)}` : ''}`);
  if (h.persen !== null && h.persen !== undefined) bag.push(`${h.persen}% jam shift`);
  if (h.telatMenit) bag.push(`terlambat ${h.telatMenit} mnt`);
  if (h.pulangCepatMenit) bag.push(`pulang cepat ${h.pulangCepatMenit} mnt`);
  if (h.libur) bag.push(h.libur.nama + (h.libur.status === 'buka' ? ' (cabang buka)' : ''));
  if (h.masukSaatLibur) bag.push('masuk saat libur nasional');
  if (h.pengajuan) bag.push(h.pengajuan.adaSurat ? 'dengan surat' : 'tanpa surat');
  return bag.join(' · ');
};

const Baris = ({ d }) => {
  const [buka, setBuka] = useState(false);
  const r = d.rekap;
  const g = d.gaji;
  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox"><IconButton size="small" onClick={() => setBuka(!buka)}>{buka ? <KeyboardArrowUp /> : <KeyboardArrowDown />}</IconButton></TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{d.user.nama}</TableCell>
        {KOLOM.map(([k]) => <TableCell key={k} align="center" sx={{ color: r[k] ? 'text.primary' : 'text.disabled' }}>{r[k] || 0}</TableCell>)}
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          {d.user.gajiPokok ? (
            <Tooltip title={`Gaji ${rupiah(g.nominal)} − potongan ${rupiah(g.potongan)} + tambahan ${rupiah(g.tambahan)}`}>
              <span><b>{rupiah(g.jumlah)}</b></span>
            </Tooltip>
          ) : <Typography variant="caption" color="text.secondary">gaji pokok kosong</Typography>}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={KOLOM.length + 3} sx={{ p: 0, border: buka ? undefined : 0 }}>
          <Collapse in={buka} unmountOnExit>
            <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
              {d.user.gajiPokok > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Hitung gaji ({g.tipeGaji}, tarif harian {rupiah(g.tarifHarian)})</Typography>
                  <Typography variant="body2">Gaji: {rupiah(g.nominal)}</Typography>
                  {g.rincianPotongan.map((p) => <Typography key={p.label} variant="body2" color="error.main">− {p.label} ({p.jumlah}×): {rupiah(p.nilai)}</Typography>)}
                  {g.rincianTambahan.map((p) => <Typography key={p.label} variant="body2" color="success.main">+ {p.label} ({p.jumlah}×): {rupiah(p.nilai)}</Typography>)}
                  <Typography variant="body2" fontWeight={700}>Diterima: {rupiah(g.jumlah)}</Typography>
                  {g.perluDicek > 0 && <Alert severity="warning" sx={{ mt: 1 }}>{g.perluDicek} hari lupa absen pulang belum dihitung. Perbaiki jam pulang di Presensi bila perlu.</Alert>}
                </Box>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {d.hari.map((h) => (
                  <Box key={h.tanggal} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ width: 110 }}>{labelTgl(h.tanggal)}</Typography>
                    <Chip size="small" color={WARNA_STATUS[h.status]} label={LABEL_STATUS[h.status] || h.status} sx={{ minWidth: 110 }} />
                    <Typography variant="caption" color="text.secondary">{ketHari(h)}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const TabKehadiran = ({ cabang }) => {
  const hariIni = new Date();
  const [dari, setDari] = useState(tglIso(new Date(hariIni.getFullYear(), hariIni.getMonth(), 1)));
  const [sampai, setSampai] = useState(tglIso(new Date(hariIni.getFullYear(), hariIni.getMonth() + 1, 0)));
  const { data, error, isLoading } = useSWR(dari && sampai ? denganCabang('/absensi/kehadiran', cabang, { dari, sampai }) : null, ambil);
  const daftar = data?.data || [];
  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField type="date" size="small" label="Dari" value={dari} onChange={(e) => setDari(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField type="date" size="small" label="Sampai" value={sampai} onChange={(e) => setSampai(e.target.value)} InputLabelProps={{ shrink: true }} />
          <Typography variant="body2" color="text.secondary">Maks. 62 hari. Status dihitung dari jadwal, absen, pengajuan & hari libur.</Typography>
        </Box>
        {error && <Alert severity="error">{pesanGalat(error)}</Alert>}
        {isLoading && <Typography color="text.secondary">Memuat…</Typography>}
        {data && !daftar.length && <Alert severity="info">Belum ada akun karyawan di cabang ini.</Alert>}
        {daftar.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Karyawan</TableCell>
                  {KOLOM.map(([k, l]) => <TableCell key={k} align="center">{l}</TableCell>)}
                  <TableCell align="right">Gaji diterima</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{daftar.map((d) => <Baris key={d.user.uuid} d={d} />)}</TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default TabKehadiran;
